import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

/**
 * Time-based progress bar for the "Aggiorna mercato" action.
 * The market scan is a single blocking request (no server-side streaming), so we
 * drive a determinate bar that eases toward ~95% over `estimatedSec` and snaps to
 * 100% when `running` turns false. Shows elapsed + estimated remaining time.
 */
export default function MarketProgress({ running, estimatedSec = 40, lang = "it", label, className = "" }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [pct, setPct] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [visible, setVisible] = useState(false);
  const startRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (running) {
      setVisible(true);
      setPct(0);
      setElapsed(0);
      startRef.current = Date.now();
      timerRef.current = setInterval(() => {
        const e = (Date.now() - startRef.current) / 1000;
        setElapsed(e);
        // Asymptotic ease: ~89% at estimatedSec, capped at 95% until completion.
        setPct(Math.min(95, 100 * (1 - Math.exp(-e / (estimatedSec * 0.45)))));
      }, 250);
      return () => clearInterval(timerRef.current);
    }
    if (visible) {
      clearInterval(timerRef.current);
      setPct(100);
      const t = setTimeout(() => { setVisible(false); setPct(0); }, 700);
      return () => clearTimeout(t);
    }
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;
  const remaining = Math.max(0, Math.ceil(estimatedSec - elapsed));
  const done = pct >= 100;
  const timeLabel = done
    ? L("Completato", "Done")
    : remaining > 0
      ? L(`~${remaining}s rimanenti · ${Math.floor(elapsed)}s trascorsi`, `~${remaining}s left · ${Math.floor(elapsed)}s elapsed`)
      : L(`quasi completato · ${Math.floor(elapsed)}s`, `almost done · ${Math.floor(elapsed)}s`);

  return (
    <div className={`mt-3 ${className}`} data-testid="market-progress">
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
        <span className="flex items-center gap-1.5 font-medium">
          {!done && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {label || L("Analisi del mercato in corso…", "Analyzing market…")}
        </span>
        <span data-testid="market-progress-time" className="font-data tabular-nums">{timeLabel}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }} data-testid="market-progress-bar" />
      </div>
    </div>
  );
}
